import { forwardRef } from "react";
import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#B8934A] disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap";

const PRIMARY = "bg-[#1F3D2E] hover:bg-[#2A4A3A] text-white shadow-sm";
const OUTLINE =
  "border border-[#1F3D2E] hover:bg-[#1F3D2E]/5 text-[#1F3D2E]";

const SIZES = {
  md: "px-6 py-3",
  lg: "px-7 py-3.5",
} as const;

type CommonProps = {
  variant?: "primary" | "outline";
  size?: keyof typeof SIZES;
  className?: string;
};

export type CTAButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & CommonProps;
export type CTAAnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & CommonProps;

const variantClass = (variant: CommonProps["variant"]) =>
  variant === "outline" ? OUTLINE : PRIMARY;

export const CTAButton = forwardRef<HTMLButtonElement, CTAButtonProps>(
  ({ className, variant = "primary", size = "md", children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(BASE, variantClass(variant), SIZES[size], className)}
      {...props}
    >
      {children}
    </button>
  )
);
CTAButton.displayName = "CTAButton";

export const CTAAnchor = forwardRef<HTMLAnchorElement, CTAAnchorProps>(
  ({ className, variant = "primary", size = "md", children, ...props }, ref) => (
    <a
      ref={ref}
      className={cn(BASE, variantClass(variant), SIZES[size], className)}
      {...props}
    >
      {children}
    </a>
  )
);
CTAAnchor.displayName = "CTAAnchor";
